import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, query, where, getDocs, addDoc } from "firebase/firestore";
import db from "../Firebase";
import { CardRenderer, getTheme, CARD_FONTS } from "../CardThemes";
import { trackEvent, detectSource, trackSource } from "../analytics";
import { useAuth } from "../AuthContext";

export default function VisitingCard() {
    const { id } = useParams();
    const [cardData, setCardData]         = useState(null);
    const [loading, setLoading]           = useState(true);
    const [saved, setSaved]               = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [pendingResumeUrl, setPendingResumeUrl] = useState(null);
    const [signingIn, setSigningIn]       = useState(false);
    const { user, signInWithGoogle }      = useAuth();

    useEffect(() => {
        const fetchCard = async () => {
            try {
                let snapshot = await getDocs(query(collection(db, "cards"), where("slug", "==", id)));
                if (snapshot.empty) snapshot = await getDocs(query(collection(db, "cards"), where("phone", "==", id)));
                if (!snapshot.empty) {
                    const data = snapshot.docs[0].data();
                    setCardData(data);
                    const slug = data.slug || id;
                    const src = detectSource();
                    await trackEvent(slug, "views");
                    trackSource(slug, src);
                }
            } catch (err) { console.error("Error fetching card:", err); }
            finally { setLoading(false); }
        };
        fetchCard();
    }, [id]);

    // After Google sign-in, auto-open the pending resume
    useEffect(() => {
        if (!user || !pendingResumeUrl) return;
        const url = pendingResumeUrl;
        setPendingResumeUrl(null);
        setShowLoginModal(false);
        saveResumeView(url, user, cardData).then(() => {
            window.open(url, "_blank", "noopener,noreferrer");
        });
    }, [user]);

    const saveResumeView = async (url, viewer, card) => {
        if (!viewer || !card) return;
        try {
            await addDoc(collection(db, "resume_views"), {
                viewerUid:        viewer.uid,
                viewerEmail:      viewer.email || "",
                viewerName:       viewer.displayName || "",
                cardOwnerUserId:  card.userId || "",
                cardSlug:         card.slug || id,
                viewedAt:         new Date(),
            });
        } catch (err) {
            console.error("Resume view log failed:", err);
        }
    };

    const handleResumeClick = async (url) => {
        if (!user) {
            setPendingResumeUrl(url);
            setShowLoginModal(true);
            return;
        }
        await saveResumeView(url, user, cardData);
        window.open(url, "_blank", "noopener,noreferrer");
    };

    // Intercept resume link clicks at the container level
    const handleCardAreaClick = (e) => {
        const link = e.target.closest("a");
        if (!link) return;
        const href = link.getAttribute("href") || "";
        const resumeUrl = cardData?.resumeUrl || "";
        const isResume = resumeUrl && (href === resumeUrl || href.includes("resumes%2F") || href.includes("/resumes/"));
        if (isResume) {
            e.preventDefault();
            handleResumeClick(resumeUrl || href);
        }
    };

    const handleGoogleLogin = async () => {
        setSigningIn(true);
        try { await signInWithGoogle(); }
        catch { setSigningIn(false); }
    };

    const downloadVCard = () => {
        if (!cardData) return;
        const vcf = [
            "BEGIN:VCARD", "VERSION:3.0",
            `FN:${cardData.name}`,
            `TEL:${cardData.phone}`,
            `EMAIL:${cardData.email}`,
            cardData.designation ? `TITLE:${cardData.designation}` : "",
            cardData.company ? `ORG:${cardData.company}` : "",
            cardData.website ? `URL:${cardData.website}` : "",
            cardData.location ? `ADR:;;${cardData.location};;;;` : "",
            "END:VCARD",
        ].filter(Boolean).join("\n");
        const blob = new Blob([vcf], { type: "text/vcard" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${cardData.name.replace(/\s+/g, "_")}.vcf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        trackEvent(cardData.slug || id, "saves");
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
    };

    if (loading) {
        return (
            <div style={{ minHeight: "100vh", width: "100%", maxWidth: "100vw", overflowX: "hidden", background: "#0A0A0A", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(212,175,55,0.5)", fontFamily: "serif", letterSpacing: "3px" }}>
                Loading…
            </div>
        );
    }

    if (!cardData) return <NotFoundRedirect />;

    const themeId = cardData.theme || "dark";
    const t = getTheme(themeId);

    return (
        <>
            <style>{CARD_FONTS}</style>
            <div style={{ minHeight: "100vh", width: "100%", maxWidth: "100vw", overflowX: "hidden", background: t.pageBg || "#0a0a0a" }}>
                {/* onClick interceptor for resume links */}
                <div
                    style={{ width: "100%", maxWidth: "480px", margin: "0 auto", padding: "24px 14px 32px", boxSizing: "border-box" }}
                    onClick={handleCardAreaClick}
                >
                    <CardRenderer themeId={themeId} cardData={cardData} onSave={downloadVCard} saved={saved} onVideoClick={cardData.promoVideoUrl ? () => setShowVideoModal(true) : undefined} />
                    <p style={{ padding: "8px 0 0", textAlign: "center", fontSize: "11px", color: "rgba(128,128,128,0.4)", letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: "300" }}>
                        Digital Card · myprofilecorner.com
                    </p>
                </div>
            </div>

            {/* Video modal */}
            {showVideoModal && cardData.promoVideoUrl && (
                <VideoModal
                    url={cardData.promoVideoUrl}
                    type={cardData.promoVideoType || "youtube"}
                    onClose={() => setShowVideoModal(false)}
                />
            )}

            {/* Login modal for resume access */}
            {showLoginModal && (
                <div
                    style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
                    onClick={() => setShowLoginModal(false)}
                >
                    <div
                        style={{ background: "#fff", borderRadius: "24px", width: "100%", maxWidth: "360px", overflow: "hidden", boxShadow: "0 32px 80px rgba(0,0,0,0.4)" }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{ background: "linear-gradient(135deg,#0D0B1A,#1A1535)", padding: "28px 24px 24px", textAlign: "center", position: "relative" }}>
                            <button
                                onClick={() => setShowLoginModal(false)}
                                style={{ position: "absolute", top: 14, right: 14, background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "50%", width: 28, height: 28, color: "#fff", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                            >×</button>
                            <div style={{ fontSize: 36, marginBottom: 10 }}>📄</div>
                            <h2 style={{ margin: "0 0 6px", color: "#fff", fontSize: 20, fontWeight: 800 }}>Sign in to view Resume</h2>
                            <p style={{ margin: 0, color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
                                A quick sign-in is required to access this document
                            </p>
                        </div>

                        {/* Body */}
                        <div style={{ padding: "24px" }}>
                            <button
                                onClick={handleGoogleLogin}
                                disabled={signingIn}
                                style={{ width: "100%", padding: "13px 16px", borderRadius: 14, border: "1.5px solid #e5e7eb", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, fontSize: 15, fontWeight: 700, color: "#111", cursor: signingIn ? "not-allowed" : "pointer", opacity: signingIn ? 0.7 : 1 }}
                            >
                                {/* Google G */}
                                <svg width="18" height="18" viewBox="0 0 24 24">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                </svg>
                                {signingIn ? "Signing in…" : "Continue with Google"}
                            </button>
                            <p style={{ margin: "14px 0 0", textAlign: "center", fontSize: 11, color: "#d1d5db" }}>
                                Your info is only used to notify the card owner who viewed their resume
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function getYoutubeId(url) {
    const m = (url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return m ? m[1] : null;
}

function VideoModal({ url, type, onClose }) {
    const ytId = type === "youtube" ? getYoutubeId(url) : null;
    return (
        <div
            style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
            onClick={onClose}
        >
            <button
                onClick={onClose}
                style={{ position: "absolute", top: 16, right: 16, width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            >×</button>
            <div style={{ width: "100%", maxWidth: "600px" }} onClick={e => e.stopPropagation()}>
                {ytId ? (
                    <div style={{ position: "relative", paddingBottom: "56.25%", borderRadius: "16px", overflow: "hidden", background: "#000" }}>
                        <iframe
                            src={`https://www.youtube.com/embed/${ytId}?autoplay=1`}
                            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
                            allow="autoplay; encrypted-media; fullscreen"
                            allowFullScreen
                            title="Promo Video"
                        />
                    </div>
                ) : (
                    <video
                        src={url}
                        controls
                        autoPlay
                        style={{ width: "100%", borderRadius: "16px", maxHeight: "80vh", background: "#000" }}
                    />
                )}
            </div>
        </div>
    );
}

function NotFoundRedirect() {
    const [seconds, setSeconds] = useState(5);
    const navigate = useNavigate();

    useEffect(() => {
        const interval = setInterval(() => {
            setSeconds(prev => {
                if (prev <= 1) { clearInterval(interval); navigate("/"); return 0; }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [navigate]);

    return (
        <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #0f3460 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", fontFamily: "sans-serif" }}>
            <div style={{ background: "#fff", borderRadius: "24px", padding: "3rem 2.5rem", width: "100%", maxWidth: "400px", textAlign: "center", boxShadow: "0 40px 80px rgba(0,0,0,0.5)" }}>
                <div style={{ fontSize: "5rem", fontWeight: 900, background: "linear-gradient(135deg, #6366f1, #4f46e5)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", lineHeight: 1, marginBottom: "0.5rem" }}>404</div>
                <h1 style={{ fontSize: "1.5rem", fontWeight: 800, color: "#1a1a2e", margin: "0 0 0.5rem" }}>Card not found</h1>
                <p style={{ fontSize: "0.9rem", color: "#6b7280", margin: "0 0 2rem" }}>This digital card doesn't exist.</p>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.6rem", marginBottom: "1.5rem" }}>
                    <div style={{ width: 64, height: 64, borderRadius: "50%", border: "4px solid #6366f1", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0f0ff", fontSize: "1.5rem", fontWeight: 900, color: "#6366f1", margin: "0 auto 0.6rem" }}>{seconds}</div>
                    <p style={{ fontSize: "0.82rem", color: "#9ca3af", margin: 0 }}>Redirecting to home…</p>
                </div>
                <button onClick={() => navigate("/")} style={{ padding: "0.85rem 2rem", background: "linear-gradient(135deg, #6366f1, #4f46e5)", color: "#fff", border: "none", borderRadius: "12px", fontSize: "0.95rem", fontWeight: 700, cursor: "pointer" }}>Go Home Now →</button>
            </div>
        </div>
    );
}
